/**
 * API Service - Backend API calls for Notes, Gallery, Favorites, Blogs
 * Replaces localStorage with MongoDB via REST API
 */
import { showApiError } from './apiErrorHandler';
import { API_URL } from './apiConfig';


// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('session_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// ============ NOTES API ============

export interface NoteImage {
  id: string;
  imageData: string;
  addedAt: string;
}

export interface Note {
  id: string;
  title: string;
  content: string;
  category: string;
  isPinned: boolean;
  images: NoteImage[];
  createdAt: string;
  updatedAt: string;
}

export const notesApi = {
  async getAll(): Promise<Note[]> {
    try {
      const res = await fetch(`${API_URL}/api/notes`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        showApiError(null, res.status);
        return [];
      }
      const data = await res.json();
      return data.notes || [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  async create(note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>): Promise<Note | null> {
    try {
      const res = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(note)
      });
      if (!res.ok) {
        showApiError(null, res.status, 'नोट बनाने में समस्या हुई');
        return null;
      }
      const data = await res.json();
      return data.note;
    } catch (error) {
      showApiError(error, undefined, 'नोट बनाने में समस्या हुई');
      return null;
    }
  },

  async update(noteId: string, updates: Partial<Note>): Promise<Note | null> {
    try {
      const res = await fetch(`${API_URL}/api/notes/${noteId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      if (!res.ok) {
        showApiError(null, res.status, 'नोट अपडेट करने में समस्या हुई');
        return null;
      }
      const data = await res.json();
      return data.note;
    } catch (error) {
      showApiError(error, undefined, 'नोट अपडेट करने में समस्या हुई');
      return null;
    }
  },

  async delete(noteId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status, 'नोट हटाने में समस्या हुई');
      return res.ok;
    } catch (error) {
      showApiError(error, undefined, 'नोट हटाने में समस्या हुई');
      return false;
    }
  }
};

// ============ GALLERY API ============

export interface GalleryCategory {
  id: string;
  name: string;
  hindiName: string;
  description: string;
  coverImage?: string;
}

export interface GalleryImage {
  id: string;
  title: string;
  categoryId: string;
  imageData: string;
  uploadedAt: string;
  tags: string[];
}

export const galleryApi = {
  async getCategories(): Promise<GalleryCategory[]> {
    try {
      const res = await fetch(`${API_URL}/api/gallery/categories`);
      if (!res.ok) { showApiError(null, res.status); return []; }
      const data = await res.json();
      return data.categories || [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  async createCategory(category: Omit<GalleryCategory, 'id'>): Promise<GalleryCategory | null> {
    try {
      const res = await fetch(`${API_URL}/api/gallery/categories`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(category)
      });
      if (!res.ok) { showApiError(null, res.status, 'श्रेणी बनाने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.category;
    } catch (error) {
      showApiError(error, undefined, 'श्रेणी बनाने में समस्या हुई');
      return null;
    }
  },

  async deleteCategory(categoryId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/gallery/categories/${categoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status, 'श्रेणी हटाने में समस्या हुई');
      return res.ok;
    } catch (error) {
      showApiError(error, undefined, 'श्रेणी हटाने में समस्या हुई');
      return false;
    }
  },

  async getImages(categoryId?: string): Promise<GalleryImage[]> {
    try {
      const url = categoryId 
        ? `${API_URL}/api/gallery/images?category_id=${categoryId}`
        : `${API_URL}/api/gallery/images`;
      const res = await fetch(url);
      if (!res.ok) { showApiError(null, res.status); return []; }
      const data = await res.json();
      return data.images || [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  async uploadImage(image: Omit<GalleryImage, 'id' | 'uploadedAt'>): Promise<GalleryImage | null> {
    try {
      const res = await fetch(`${API_URL}/api/gallery/images`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(image)
      });
      if (!res.ok) { showApiError(null, res.status, 'चित्र अपलोड करने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.image;
    } catch (error) {
      showApiError(error, undefined, 'चित्र अपलोड करने में समस्या हुई');
      return null;
    }
  },

  async deleteImage(imageId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/gallery/images/${imageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status, 'चित्र हटाने में समस्या हुई');
      return res.ok;
    } catch (error) {
      showApiError(error, undefined, 'चित्र हटाने में समस्या हुई');
      return false;
    }
  }
};

// ============ FAVORITES API ============

export interface FavoriteItem {
  id: string;
  type: 'ebook' | 'gallery' | 'audio';
  title: string;
  thumbnail?: string;
  subtitle?: string;
  addedAt: string;
}

export const favoritesApi = {
  async getAll(): Promise<FavoriteItem[]> {
    try {
      const res = await fetch(`${API_URL}/api/favorites`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) { showApiError(null, res.status); return []; }
      const data = await res.json();
      return data.favorites || [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  async add(item: Omit<FavoriteItem, 'addedAt'>): Promise<FavoriteItem | null> {
    try {
      const res = await fetch(`${API_URL}/api/favorites`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(item)
      });
      if (!res.ok) { showApiError(null, res.status, 'पसंदीदा में जोड़ने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.favorite;
    } catch (error) {
      showApiError(error, undefined, 'पसंदीदा में जोड़ने में समस्या हुई');
      return null;
    }
  },

  async remove(itemType: string, itemId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/favorites/${itemType}/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status, 'पसंदीदा हटाने में समस्या हुई');
      return res.ok;
    } catch (error) {
      showApiError(error, undefined, 'पसंदीदा हटाने में समस्या हुई');
      return false;
    }
  },

  async check(itemType: string, itemId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/favorites/check/${itemType}/${itemId}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) return false;
      const data = await res.json();
      return data.isFavorited;
    } catch (error) {
      return false;
    }
  }
};

// ============ BLOGS API ============

export interface BlogAuthor {
  id: string;
  name: string;
  avatar?: string;
}

export interface BlogComment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: string;
  likes: string[];
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage?: string;
  author: BlogAuthor;
  category: string;
  tags: string[];
  status: 'draft' | 'published';
  views: number;
  likes: string[];
  bookmarkedBy: string[];
  comments: BlogComment[];
  readingTimeMinutes: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
}

export const blogsApi = {
  async getAll(status?: string, category?: string): Promise<BlogPost[]> {
    try {
      let url = `${API_URL}/api/blogs`;
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (category) params.append('category', category);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url);
      if (!res.ok) { showApiError(null, res.status); return []; }
      const data = await res.json();
      return data.blogs || [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  async getById(blogId: string): Promise<BlogPost | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}`);
      if (!res.ok) { showApiError(null, res.status); return null; }
      const data = await res.json();
      return data.blog;
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  async create(blog: {
    title: string;
    excerpt: string;
    content: string;
    featuredImage?: string;
    category: string;
    tags: string[];
    status: string;
  }): Promise<BlogPost | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(blog)
      });
      if (!res.ok) { showApiError(null, res.status, 'ब्लॉग बनाने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.blog;
    } catch (error) {
      showApiError(error, undefined, 'ब्लॉग बनाने में समस्या हुई');
      return null;
    }
  },

  async update(blogId: string, updates: Partial<BlogPost>): Promise<BlogPost | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      if (!res.ok) { showApiError(null, res.status, 'ब्लॉग अपडेट करने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.blog;
    } catch (error) {
      showApiError(error, undefined, 'ब्लॉग अपडेट करने में समस्या हुई');
      return null;
    }
  },

  async delete(blogId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status, 'ब्लॉग हटाने में समस्या हुई');
      return res.ok;
    } catch (error) {
      showApiError(error, undefined, 'ब्लॉग हटाने में समस्या हुई');
      return false;
    }
  },

  async toggleLike(blogId: string): Promise<{ liked: boolean; likesCount: number } | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}/like`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (error) {
      return null;
    }
  },

  async toggleBookmark(blogId: string): Promise<{ bookmarked: boolean } | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}/bookmark`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (error) {
      return null;
    }
  },

  async addComment(blogId: string, content: string): Promise<BlogComment | null> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}/comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content })
      });
      if (!res.ok) { showApiError(null, res.status, 'टिप्पणी जोड़ने में समस्या हुई'); return null; }
      const data = await res.json();
      return data.comment;
    } catch (error) {
      showApiError(error, undefined, 'टिप्पणी जोड़ने में समस्या हुई');
      return null;
    }
  },

  async deleteComment(blogId: string, commentId: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${blogId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) showApiError(null, res.status);
      return res.ok;
    } catch (error) {
      showApiError(error);
      return false;
    }
  },

  async getCategories(): Promise<BlogCategory[]> {
    try {
      const res = await fetch(`${API_URL}/api/blogs/categories`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.categories || [];
    } catch (error) {
      return [];
    }
  }
};
